import {
  NumericAxis,
  NumberRange,
  EAxisAlignment,
  EAutoRange,
  NumericLabelProvider,
  type SciChartSurface,
  type TSciChart,
} from 'scichart';
import { formatNumber } from 'utils/number';
import {
  AXIS_TITLE_FONT_SIZE,
  AXIS_LABEL_FONT_SIZE,
} from 'components/charts/scichart';

// Shared depth axes for the 2D Profile + Gun Barrel so gridlines align. Series
// plot in TVDSS data-space; left axis labels as TVD, right mirror as TVDSS.

// Primary axis id the series bind to (TVDSS data-space, labelled as TVD). Kept
// as TVDSS_AXIS_ID so series references don't churn.
export const TVDSS_AXIS_ID = 'tvdss';
export const TVD_AXIS_ID = 'tvd';
const MAX_AUTO_TICKS = 8;

const fmt = (v: number): string => formatNumber(v, 0, 0);

interface SharedDepthAxesOptions {
  /** A zoom or drag on the right TVDSS mirror also moves the primary axis the
   *  series bind to; without it only the mirror's labels move. */
  mirrorDrivesPrimary?: boolean;
}

// Add the left TVD axis (primary, where data binds) + right TVDSS mirror.
// `elevRef` supplies elevation for TVD labels (a ref since data arrives later).
export function addSharedDepthAxes(
  surface: SciChartSurface,
  wasmContext: TSciChart,
  elevRef: { current: number },
  { mirrorDrivesPrimary = false }: SharedDepthAxesOptions = {}
): void {
  // Left, primary: data plots here in TVDSS coordinates, labelled as TVD.
  const tvdLabel = new NumericLabelProvider();
  tvdLabel.formatLabel = (v: number) => fmt(elevRef.current - v);
  surface.yAxes.add(
    new NumericAxis(wasmContext, {
      id: TVDSS_AXIS_ID,
      axisTitle: 'TVD (ft)',
      axisTitleStyle: { fontSize: AXIS_TITLE_FONT_SIZE },
      labelStyle: { fontSize: AXIS_LABEL_FONT_SIZE },
      axisAlignment: EAxisAlignment.Left,
      autoRange: EAutoRange.Never,
      drawMinorGridLines: false,
      maxAutoTicks: MAX_AUTO_TICKS,
      labelProvider: tvdLabel,
    })
  );

  // Right mirror: raw TVDSS labels, no gridlines.
  const tvdssLabel = new NumericLabelProvider();
  tvdssLabel.formatLabel = fmt;
  const tvdss = new NumericAxis(wasmContext, {
    id: TVD_AXIS_ID,
    axisTitle: 'TVDSS (ft)',
    axisTitleStyle: { fontSize: AXIS_TITLE_FONT_SIZE },
    labelStyle: { fontSize: AXIS_LABEL_FONT_SIZE },
    axisAlignment: EAxisAlignment.Right,
    autoRange: EAutoRange.Never,
    drawMajorGridLines: false,
    drawMinorGridLines: false,
    maxAutoTicks: MAX_AUTO_TICKS,
    labelProvider: tvdssLabel,
  });
  surface.yAxes.add(tvdss);

  // Keep the right TVDSS mirror locked to the primary range on zoom/pan.
  const primary = surface.yAxes.getById(TVDSS_AXIS_ID);
  primary.visibleRangeChanged.subscribe((args) => {
    if (args) tvdss.visibleRange = args.visibleRange;
  });
  if (!mirrorDrivesPrimary) return;
  // The wheel and drag modifiers move only the axis under the mouse, so the
  // mirror hands its range back; equal ranges stop the two echoing each other.
  tvdss.visibleRangeChanged.subscribe((args) => {
    if (args && !args.visibleRange.equals(primary.visibleRange)) {
      primary.visibleRange = args.visibleRange;
    }
  });
}

// Apply the shared TVDSS domain to both depth axes (call from renderData).
export function setSharedDepthRange(
  surface: SciChartSurface,
  domain: [number, number] | null
): void {
  if (!domain) return;
  const range = new NumberRange(domain[0], domain[1]);
  const tvdAxis = surface.yAxes.getById(TVDSS_AXIS_ID);
  // TVD labels are `datum − TVDSS`, but the datum (elevRef) is only known once
  // data arrives. The label provider caches text per tick value, so the zero
  // tick keeps the "0" it computed on the pre-data render (datum 0) while every
  // other tick reformats. Invalidate so the zero-crossing reformats too.
  (tvdAxis.labelProvider as NumericLabelProvider).invalidateCache();
  tvdAxis.visibleRange = range;
  const tvd = surface.yAxes.getById(TVD_AXIS_ID);
  if (tvd) tvd.visibleRange = range;
}
